import { FunctionComponent, useCallback } from 'react'
import TeaserMiddleToiWrapper from '@components/TeaserMiddleToi/Wrapper'
import TextWrapper from '@components/TeaserMiddleToi/TextWrapper'
import Label from '@components/TeaserMiddleToi/Label'
import ImageComponent from '@components/TeaserMiddleToi/Image'
import Catchword from '@components/TeaserMiddleToi/Catchword'
import BlickPlusLogo from '@components/TeaserMiddleToi/BlickPlusLogo'
import Title from '@components/TeaserMiddleToi/Title'
import TeaserAnimatedPreview from '@components/TeaserAnimatedPreview'
import VideoLabel from '@components/VideoLabel'
import SportLabelContent from '@components/SportsFooter/SportLabelContent'
import useTracking, { TrackingFnType } from '@hooks/useTracking'
import useRefDelegate from '@hooks/useRefDelegate'
import useAnimatedPreview from '@hooks/useAnimatedPreview'
import useViewportTracking from '@hooks/useViewportTracking'
import useSportsEvent from '@hooks/useSportsEvent'
import { useQueryClient } from '@tanstack/react-query'
import { stripHtml } from '@hooks/useTracking/utils'
import { triggerAureusImpression } from '@utils/aureus'
import styled, { css, useTheme } from 'styled-components'
import { LIVE_EVENT_DATA_SPORT_TYPES } from '@utils/formatters/common'
import { LiveEventDataSportTypes } from '@utils/formatters/liveEvent'
import { transformDataForSportsComponents } from '@components/SportsFooter/utils'
import { CookWidget, JSONTypeForCookWidget } from '@widgets/types'
import { Image, Link, TargetContentType, TeaserVideo } from '@utils/cook/types'
import HockeySoccerSportsFooter from '@components/SportsFooter/HockeySoccer'
import useShowBlinkerAndLoudspeaker from '@hooks/useShowBlinkerAndLoudspeaker'
import { FeatureFlagsEntries } from '@hooks/useABTestValue'
import config from '@config'
import LoudSpeaker from '@components/TeaserMiddleToi/LoudSpeaker'
import Blinker from '@components/TeaserMiddleToi/Blinker'

const {
  abTest: { windowKey },
} = config

export interface TeaserMiddleToiCommonProps {
  accentColor: string
  title: string
  catchword?: string
  isPlus?: boolean
  articleId?: string
  targetContentType: TargetContentType
  track?: boolean
  link: Link
  image: Image<'16_9'>
  section: {
    name: string
    uniqueName: string
  }
  contentOrigin?: string
  video?: TeaserVideo
  aureusOfferId?: string
  hasSpecialLabel?: boolean
}

export interface TeaserMiddleToiNoCommercialProps {
  commercialType?: undefined
  labelText?: string
}

export interface TeaserMiddleToiNoSportProps {
  typeOfSport?: undefined
}

export interface TeaserMiddleToiSportProps {
  typeOfSport: Extract<LiveEventDataSportTypes, 'soccer' | 'hockey'>
  matchId: string
}

export type TeaserMiddleToiMaybeSportProps =
  | TeaserMiddleToiNoSportProps
  | TeaserMiddleToiSportProps

export type TeaserMiddleToiAPIProps = TeaserMiddleToiCommonProps &
  TeaserMiddleToiNoCommercialProps &
  TeaserMiddleToiMaybeSportProps

const StyledVideoLabel = styled(VideoLabel)`
  ${({
    theme: {
      spacing: { spacing8 },
    },
  }) => css`
    position: absolute;
    bottom: ${spacing8};
    left: ${spacing8};
  `}
`

const StyledHockeySoccerSportsFooter = styled(HockeySoccerSportsFooter)`
  ${({
    theme: {
      colors: { staticNeutral900 },
    },
  }) => css`
    background-color: ${staticNeutral900};
  `}
`

const TeaserMiddleToi: FunctionComponent<TeaserMiddleToiAPIProps> = (props) => {
  const {
    accentColor,
    title,
    link,
    image,
    isPlus,
    catchword,
    targetContentType,
    typeOfSport,
    articleId,
    contentOrigin,
    track,
    video,
    aureusOfferId,
    commercialType,
    hasSpecialLabel,
  } = props
  const queryClient = useQueryClient()
  const isValidSport =
    !!typeOfSport &&
    (
      [
        LIVE_EVENT_DATA_SPORT_TYPES.SOCCER,
        LIVE_EVENT_DATA_SPORT_TYPES.HOCKEY,
      ] as LiveEventDataSportTypes[]
    ).includes(typeOfSport)
  const showCatchword = !!isPlus || !!catchword
  const isCommercial = !!commercialType
  const hasLabelText = !isCommercial && !!props.labelText
  const labelType = useShowBlinkerAndLoudspeaker()
  const hasLabelForABCTest = hasLabelText && !!hasSpecialLabel
  const shouldShowBlinker = [labelType === 'B', hasLabelForABCTest].every(
    Boolean
  )

  const shouldShowLoudspeaker = hasLabelForABCTest && !labelType

  const shouldShowSpecialLabel = shouldShowBlinker || shouldShowLoudspeaker

  const { data: sportsEventData } = useSportsEvent({
    matchId: isValidSport ? props.matchId : '',
    typeOfSport: isValidSport ? typeOfSport : 'NO_SPORT',
    shouldRefetch: true,
  })

  const sportsData = sportsEventData
    ? transformDataForSportsComponents(sportsEventData)
    : undefined
  const shouldShowSportsComponents =
    sportsData?.typeOfSport === LIVE_EVENT_DATA_SPORT_TYPES.SOCCER ||
    sportsData?.typeOfSport === LIVE_EVENT_DATA_SPORT_TYPES.HOCKEY

  const {
    color: {
      secondary: { greenAccessible },
    },
  } = useTheme()

  const labelColor = sportsData?.isLive
    ? undefined
    : isValidSport
      ? greenAccessible
      : accentColor

  const onImpression = useCallback<TrackingFnType>(
    ({ queryClient }) => {
      const labelType = queryClient.getQueryData<
        FeatureFlagsEntries['showBlinkerAndLoudspeaker']['values'][number]['name']
      >([windowKey, 'showBlinkerAndLoudspeaker'])
      return {
        event: 'teaser_impression',
        eventCategory: video ? 'video_teaser' : 'article_teaser',
        eventLabel: `${articleId}:${stripHtml(title)}`,
        eventAction: 'impression',
        id: articleId,
        is_branded: !!contentOrigin,
        link_url: link.href,
        ...(hasLabelForABCTest
          ? { teaser_type: `variant${labelType || 'A'}` }
          : {}),
      }
    },
    [video, articleId, title, contentOrigin, link.href, hasLabelForABCTest]
  )

  const onClick = useCallback<TrackingFnType>(
    ({ queryClient }) => {
      const labelType = queryClient.getQueryData<
        FeatureFlagsEntries['showBlinkerAndLoudspeaker']['values'][number]['name']
      >([windowKey, 'showBlinkerAndLoudspeaker'])
      return {
        event: 'teaser_click',
        eventCategory: video ? 'video_teaser' : 'article_teaser',
        eventLabel: `${articleId}:${stripHtml(title)}`,
        eventAction: 'click',
        id: articleId,
        is_branded: !!contentOrigin,
        link_url: link.href,
        ...(hasLabelForABCTest
          ? { teaser_type: `variant${labelType || 'A'}` }
          : {}),
      }
    },
    [articleId, contentOrigin, title, video, link.href, hasLabelForABCTest]
  )

  const trackedOnImpression = useTracking(onImpression)
  const trackedOnClick = useTracking(onClick)

  const isABtest = hasLabelForABCTest
  const shouldBeTracked = !!track || isABtest

  const finalOnImpression = useCallback(() => {
    triggerAureusImpression({
      aureusOfferId,
      queryClient,
    })

    if (shouldBeTracked) {
      trackedOnImpression()
    }
  }, [aureusOfferId, queryClient, trackedOnImpression, shouldBeTracked])

  const clickableProps = {
    aureusOfferId,
    ...(shouldBeTracked
      ? {
          onClick: trackedOnClick,
        }
      : {}),
    ...link,
  }

  const ref = useRefDelegate(
    useAnimatedPreview({
      video,
      articleUrl: clickableProps.href,
    }),
    useViewportTracking({
      onImpression: finalOnImpression,
      track: true,
    })
  )
  return (
    <TeaserMiddleToiWrapper {...clickableProps} ref={ref}>
      <ImageComponent {...image}>
        {shouldShowLoudspeaker && hasLabelText && (
          <LoudSpeaker labelColor={labelColor}>{props.labelText}</LoudSpeaker>
        )}
        {!!video && (
          <TeaserAnimatedPreview video={video} articleUrl={link.href} />
        )}
        {!!props.video && (
          <StyledVideoLabel
            duration={props.video.duration}
            targetContentType={targetContentType}
          />
        )}
        <TextWrapper>
          {!shouldShowSpecialLabel && (hasLabelText || isValidSport) && (
            <Label labelColor={labelColor}>
              {shouldShowSportsComponents ? (
                <SportLabelContent sportsData={sportsData} />
              ) : hasLabelText ? (
                props.labelText
              ) : null}
            </Label>
          )}
          {shouldShowBlinker && hasLabelText && (
            <Blinker labelColor={labelColor}>{props.labelText}</Blinker>
          )}
          {showCatchword && (
            <Catchword>
              {isPlus && <BlickPlusLogo />}
              {catchword}
            </Catchword>
          )}
          <Title>{title}</Title>
        </TextWrapper>
      </ImageComponent>
      {shouldShowSportsComponents && (
        <StyledHockeySoccerSportsFooter
          sportsData={sportsData}
          viewType="full"
        />
      )}
    </TeaserMiddleToiWrapper>
  )
}

const widget = {
  kind: ['teaser-middle-toi'],
  component: TeaserMiddleToi,
} as const satisfies CookWidget

export type WidgetType = typeof widget

export type JSONWidgetType = JSONTypeForCookWidget<WidgetType>

export default widget
